import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AppVersionProvider } from './version/AppVersionProvider'
import { Layout } from './components/Layout'
import { RouteTitle } from './components/RouteTitle'
import { ProtectedRoute } from './auth/ProtectedRoute'
import { CallbackPage } from './pages/CallbackPage'
import { WifiCredentialsPage } from './pages/WifiCredentialsPage'
import { RoleBasedHome } from './pages/RoleBasedHome'
import { CompaniesPage } from './pages/CompaniesPage'
import { CoworkersPage } from './pages/CoworkersPage'
import { CoworkerEditPage } from './pages/CoworkerEditPage'
import { GuestInvitationsPage } from './pages/GuestInvitationsPage'
import { NewGuestInvitationPage } from './pages/NewGuestInvitationPage'
import { WifiNetworksPage } from './pages/WifiNetworksPage'
import { WifiNetworkFormPage } from './pages/WifiNetworkFormPage'
import { CompanyFormPage } from './pages/CompanyFormPage'
import { SchedulesPage } from './pages/SchedulesPage'
import { BuildingsPage } from './pages/BuildingsPage'
import { BuildingFormPage } from './pages/BuildingFormPage'
import { RoomFormPage } from './pages/RoomFormPage'
import { DeviceUsersPage } from './pages/DeviceUsersPage'
import { BuildingBookingsPage } from './pages/BuildingBookingsPage'
import { BuildingCalendarPage } from './pages/BuildingCalendarPage'
import { BookingFormPage } from './pages/BookingFormPage'
import { MyBookingsPage } from './pages/MyBookingsPage'
import { MyProfilePage } from './pages/MyProfilePage'
import { MyEnrollPage } from './pages/MyEnrollPage'
import { BookRoomPage } from './pages/BookRoomPage'
import { RoomBrowsePage } from './pages/RoomBrowsePage'
import { AuditLogPage } from './pages/AuditLogPage'
import { AccessActivityPage } from './pages/AccessActivityPage'
import { FaceStatusPage } from './pages/FaceStatusPage'
import { NotEnrolledPage } from './pages/NotEnrolledPage'
import { EnrollPage } from './pages/EnrollPage'
import { PrintersListPage } from './pages/PrintersListPage'
import { PrinterOnboardingWizard } from './pages/PrinterOnboardingWizard'
import { PrinterDetailPage } from './pages/PrinterDetailPage'
import { PrintCenterPage } from './pages/PrintCenterPage'
import { PrintReleasePage } from './pages/PrintReleasePage'

function PreservingNavigate({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

export function App() {
  return (
    <>
      <RouteTitle />
      <AppVersionProvider>
        <Routes>
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/wifi" element={<Navigate to="/my/wifi" replace />} />
          <Route path="/print" element={<Navigate to="/my/print" replace />} />
          <Route path="/profile" element={<Navigate to="/my/profile" replace />} />
          <Route path="/guest-invitations" element={<PreservingNavigate to="/my/guests" />} />
          <Route
            path="/guest-invitations/new"
            element={<PreservingNavigate to="/my/guests/new" />}
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <RoleBasedHome />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my/wifi"
            element={
              <ProtectedRoute>
                <Layout>
                  <WifiCredentialsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <CompaniesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/new"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <CompanyFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/:id/edit"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <CompanyFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedules"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout>
                  <SchedulesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/coworkers"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <CoworkersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/coworkers/:id/edit"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <CoworkerEditPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my/guests"
            element={
              <ProtectedRoute>
                <Layout>
                  <GuestInvitationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my/guests/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <NewGuestInvitationPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wifi-networks"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout>
                  <WifiNetworksPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wifi-networks/new"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout>
                  <WifiNetworkFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wifi-networks/:id/edit"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout>
                  <WifiNetworkFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buildings"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <BuildingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buildings/new"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <BuildingFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buildings/:id/edit"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <BuildingFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buildings/:buildingId/rooms"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <BuildingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buildings/:buildingId/rooms/new"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <RoomFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buildings/:buildingId/rooms/:roomId/edit"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <RoomFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buildings/:buildingId/readers/:readerId/device-users"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout>
                  <DeviceUsersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buildings/:id/bookings"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <BuildingBookingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/buildings/:id/calendar" element={<Navigate to="week" replace />} />
          <Route
            path="/buildings/:id/calendar/:viewMode"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <BuildingCalendarPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buildings/:id/bookings/new"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <BookingFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buildings/:id/bookings/:bookingId/edit"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <BookingFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my/bookings"
            element={
              <ProtectedRoute>
                <Layout>
                  <MyBookingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my/bookings/new"
            element={
              <ProtectedRoute roles={['admin', 'operator', 'user']}>
                <Layout>
                  <RoomBrowsePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my/bookings/:bookingId/edit"
            element={
              <ProtectedRoute roles={['admin', 'operator', 'user']}>
                <Layout>
                  <BookRoomPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  {({ darkMode, onToggleDarkMode }) => (
                    <MyProfilePage darkMode={darkMode} onToggleDarkMode={onToggleDarkMode} />
                  )}
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my/enroll"
            element={
              <ProtectedRoute roles={['user']}>
                <Layout>
                  <MyEnrollPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-log"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout>
                  <AuditLogPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/access/activity"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <AccessActivityPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/access/faces"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout>
                  <FaceStatusPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/printers"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <PrintersListPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/printers/new"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout>
                  <PrinterOnboardingWizard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/printers/:id"
            element={
              <ProtectedRoute roles={['admin', 'operator']}>
                <Layout>
                  <PrinterDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my/print"
            element={
              <ProtectedRoute>
                <Layout>
                  <PrintCenterPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/p/:printerId"
            element={
              <ProtectedRoute>
                <Layout>
                  <PrintReleasePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/not-enrolled" element={<NotEnrolledPage />} />
          {/* Public self-enrollment wizard (Access Provider Phase 3, Task 11) —
            deliberately mounted OUTSIDE <ProtectedRoute>, same as
            /not-enrolled above: a prospective coworker following the invite
            link has no Auth0 session yet. Trust is anchored on the
            per-company :token embedded in the link, not a JWT. */}
          <Route path="/enroll/:token" element={<EnrollPage />} />
        </Routes>
      </AppVersionProvider>
    </>
  )
}
